import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Stack } from 'react-bootstrap';
import { Formik, Form, FormikHelpers, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { map } from 'lodash';

import { AmountControl, FormGroupControl } from '@components/Base';
import { tOrdinalItem } from '@components/i18n';

import { FormValues } from './type';
import { useValidationSchema } from './hook';

const GROUP_PROPS = { className: 'mb-3' };

const FormForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { values, isSubmitting } = useFormikContext<FormValues>();

  return (
    <Form>
      <Card>
        <Card.Body>
          <AmountControl
            title={t('common:myself')}
            premiumName="me.premium"
            discountName="me.discountPremium"
            groupProps={GROUP_PROPS} />

          {!!values.spouse && (
            <AmountControl
              title={t('common:spouse')}
              premiumName="spouse.premium"
              discountName="spouse.discountPremium"
              groupProps={GROUP_PROPS} />
          )}

          {!!values.sons && map(values.sons, (_, idx) => (
            <AmountControl
              key={idx}
              title={tOrdinalItem(idx + 1, 'common:son')}
              premiumName={`sons.${idx}.premium`}
              discountName={`sons.${idx}.discountPremium`}
              groupProps={GROUP_PROPS}
            />
          ))}

          {!!values.daughters && map(values.daughters, (_, idx) => (
            <AmountControl
              key={idx}
              title={tOrdinalItem(idx + 1, 'common:daughter')}
              premiumName={`daughters.${idx}.premium`}
              discountName={`daughters.${idx}.discountPremium`}
              groupProps={GROUP_PROPS}
            />
          ))}

          <FormGroupControl
            name="adminFeeAmount"
            label="Admin fee"
            required
            groupProps={GROUP_PROPS}
            type="number"
          />

          <FormGroupControl required name="proof" type="file" label={t('proof')} groupProps={GROUP_PROPS} />
          <FormGroupControl required name="note" label="Note" groupProps={GROUP_PROPS} as="textarea" rows={4} />
        </Card.Body>

        <Card.Footer className="bg-white border-0 mx-auto my-4">
          <Stack direction="horizontal" gap={3}>
            <Button onClick={() => navigate(-1)} variant="outline-primary" size="lg">
              {t('common:cancel')}
            </Button>

            <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
              {t('common:update')}
            </Button>
          </Stack>
        </Card.Footer>
      </Card>
    </Form>
  );
}

type Props = {
  initialValues: FormValues;
  onSubmit: (values: FormValues) => void;
};

const HealthQuoteBuyCustomForm = ({ initialValues, onSubmit }: Props) => {
  const validationSchema = useValidationSchema();

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    setSubmitting(false);
    onSubmit(values);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      <FormForm />
    </Formik>
  );
};

export { HealthQuoteBuyCustomForm };
